import { defineComponent as _defineComponent } from 'vue'
import { User, UserAuthenticationType } from '@/models/user';
import { UserType } from '@/models/userType';
import { Customer } from '@/models/tenant';
import { usersService } from '@/services/users.service';
import { Subsidiary } from '@/models/subsidiary';
import { subsidiariesService } from '@/services/subsidiaries.service';
import { customersService } from '@/services/tenants.service';
import { currentUser, hasCurrentUserPermission } from '@/utils/permissionUtils';
import PasswordResetModal from '@/components/users/PasswordResetModal.vue';
import TableActionButton from '@/components/tableWithActions/TableActionButton.vue';
import TableWithActions from '@/components/tableWithActions/TableWithActions.vue';
import UserCreationDropdown from '@/components/users/wizards/UserCreationDropdown.vue';
import {
  openCustomerUserWizard,
  openGlobalAdminWizard,
  openServiceGlobalUserWizard,
  openServiceUserWizard,
  openSubsidiaryAdminWizard,
  openTechnologyUserWizard,
} from '@/components/users/wizards/modals';
import { filterUserType, sortByUserType } from '@/utils/table';
import { dialogService } from '@/library-services/dialog.service';
import { modalService } from '@/library-services/modal.service';
import { computed, getCurrentInstance, onBeforeMount, ref } from 'vue';
import { useI18n } from '@/composables/i18n';

interface UserItem {
  user: User;
  belongsToName: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Users',
  setup(__props) {

const { t } = useI18n();
const app = getCurrentInstance();
const usersData = ref<UserItem[]>([]);
const subsidiaries = ref<Subsidiary[]>([]);
const customers = ref<Customer[]>([]);
const isLoading = ref(false);

onBeforeMount(async () => {
  isLoading.value = true;
  await getSubsidiaries();
  await getCustomers();
  await getUsers();
  isLoading.value = false;
});

function confirmDelete(id: number, name: string): void {
  dialogService.confirmWithPromise({
    title: t('dialog.confirm_delete_title').toString(),
    message: t('confirmation.message', {
      action: t('action.delete').toString().toLowerCase(),
      entity: t('user.name').toString().toLowerCase(),
      name,
    }).toString(),
    confirmText: t('dialog.confirm_delete').toString(),
    cancelText: t(`dialog.confirm_cancel`).toString(),
    type: 'is-danger',
    onConfirm: async () => await deleteUser(id),
  });
}

function editUser(user: User) {
  if (user.isGlobalAdmin) {
    openGlobalAdminWizard(app, user, modalClose);
  } else if (user.isServiceGlobal) {
    openServiceGlobalUserWizard(app, user, modalClose);
  } else if (user.isServiceUser) {
    openServiceUserWizard(app, user, null, modalClose);
  } else if (user.isSubsidiaryAdmin) {
    openSubsidiaryAdminWizard(app, user, null, modalClose);
  } else if (user.isCustomer) {
    openCustomerUserWizard(app, user, modalClose);
  } else if (user.isTechnologyUser) {
    openTechnologyUserWizard(app, user, modalClose);
  }
}

function resetPassword(user: User) {
  modalService.compositionApi.open(
    {
      component: PasswordResetModal,
      props: {
        id: user.id,
        email: user.email,
      },
    },
    app,
  );
}

async function modalClose() {
  await getUsers();
}

function subsidiaryName(subsidiaryId: number) {
  return subsidiaries.value.find((s) => s.id === subsidiaryId)?.name ?? '-';
}

function customerName(customerId: number) {
  return customers.value.find((s) => s.id === customerId)?.name ?? '-';
}

function getBelongsToName(user: User) {
  if (user.isSubsidiaryAdmin || user.isServiceUser) {
    return subsidiaryName(user.subsidiaryId!);
  } else if (user.isCustomer) {
    return customerName(user.customerId!);
  } else {
    return '';
  }
}

async function getUsers() {
  isLoading.value = true;
  try {
    usersData.value = (await usersService.get()).map((user) => ({
      user,
      belongsToName: getBelongsToName(user),
    }));
  } finally {
    isLoading.value = false;
  }
}

async function getSubsidiaries() {
  if (hasCurrentUserPermission(UserType.SubsidiaryAdmin)) {
    subsidiaries.value = await subsidiariesService.get();
  }
}

async function getCustomers() {
  if (hasCurrentUserPermission(UserType.CustomerUser)) {
    customers.value = await customersService.get();
  }
}

async function deleteUser(id: number) {
  await usersService.delete(id).then(() => {
    usersData.value = usersData.value.filter(
      (userItem) => userItem.user.id !== id,
    );
  });
}

function filterType(userItem: UserItem, filter: string) {
  return filterUserType(userItem.user, filter);
}

function sortByType(a: UserItem, b: UserItem, isAsc: boolean) {
  return sortByUserType(a.user, b.user, isAsc);
}

function canResetPasswordOn(user: User): boolean {
  return user.authenticationType === UserAuthenticationType.Local;
}

function canEdit(editingUser: User): boolean {
  return (
    // Global Admin - can edit all users
    currentUser().isGlobalAdmin ||
    // Any user can edit itself
    currentUser().equals(editingUser) ||
    // Subsidiary Admin - can edit Customer Admin, Customer User and eService User
    (currentUser().isSubsidiaryAdmin &&
      (editingUser.isCustomer || editingUser.isServiceUser)) ||
    // Customer Admin - can edit Customer Admin and Customer User
    (currentUser().isCustomerAdmin && editingUser.isCustomer)
  );
}

const canCreateUsers = computed(() =>
  hasCurrentUserPermission(UserType.CustomerAdmin),
);

return { __sfc: true,t, app, usersData, subsidiaries, customers, isLoading, confirmDelete, editUser, resetPassword, modalClose, subsidiaryName, customerName, getBelongsToName, getUsers, getSubsidiaries, getCustomers, deleteUser, filterType, sortByType, canResetPasswordOn, canEdit, canCreateUsers, currentUser, TableActionButton, TableWithActions, UserCreationDropdown }
}

})